<template>
  <app-spinner v-if="loading" class="mt-4" />

  <div v-else class="relative overflow-hidden">
    <SwayOfferNotificationModal v-if="showEmailPrompt" @sway-offer-notification-data-entered="handleEmailAddressEntered" />

    <GiveAwayNotificationModal v-if="giveAwayProofId" :proof-id="giveAwayProofId" />

    <div v-else-if="showWebComplete" class="ma-auto pb-10" data-cy="webSwayOfferSuccessfullySubmitted">
      <v-container>
        <v-row class="mt-2">
          <v-col align="center" justify="center">
            <div class="offer-complete-circle">
              <v-icon x-large color="#009200">mdi-check-bold</v-icon>
            </div>
          </v-col>
        </v-row>

        <v-row align="center" justify="center" class="mt-8">
          <v-col cols="11" class="py-0">
            <h1 class="text-h5 text-center font-weight-light">Successfully submitted</h1>
            <div class="text-center text--secondary mt-4">What's next?</div>
            <div class="text-h5 text-center">↓</div>
          </v-col>
        </v-row>

        <v-row justify="center" class="mt-5 mx-5">
          <v-col cols="1" class="py-0">
            <v-avatar color="primary" size="40" class="mt-3 mr-3">
              <span class="white--text font-weight-bold">1</span>
            </v-avatar>
          </v-col>
          <v-col cols="10" class="pl-8 text-center">
            <v-img :src="require('@/assets/reverseFlow/web_success_step_01.png')" contain height="250" />
            <div class="text--secondary mt-3">Now the business will review your upload, and you'll be notified if it's approved or denied within 48 hours.</div>
          </v-col>
        </v-row>

        <v-row justify="center" class="mt-12 mx-5">
          <v-col cols="1" class="py-0">
            <v-avatar color="primary" size="40" class="mt-3 mr-3">
              <span class="white--text font-weight-bold">2</span>
            </v-avatar>
          </v-col>
          <v-col cols="10" class="pl-8 text-center">
            <v-img :src="require('@/assets/reverseFlow/web_success_step_02.png')" contain height="150" />
            <div class="text--secondary mt-3">
              You'll receive an email with your promo code if your upload is approved or an email with a reason of why it was denied.
            </div>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <div v-else-if="error" class="ma-auto">
      <v-container>
        <v-row align="center" justify="center" class="mt-4 mb-2">
          <v-col cols="11" class="py-0">
            <h1 class="text-h5 text-center">Whoops! That offer is no longer available.</h1>
          </v-col>
        </v-row>
      </v-container>
    </div>

    <div v-else>
      <TaskExamplesModal
        :modal-visible="showExamplesModal"
        :task-type="swayOfferTaskType"
        :hash-tags="hashtags"
        :mentions="instagramMentions || facebookMentions"
        @task-examples-modal-close="showExamplesModal = false"
      />

      <ScreenshotExamplesModal :modal-visible="showScreenshotExamplesModal" @screenshot-examples-modal-close="showScreenshotExamplesModal = false" />

      <v-row v-if="swayOffer.offer_image_url" align="center" justify="center">
        <v-col cols="12" class="py-0 text-center">
          <v-img class="p-0 d-inline-flex" :src="swayOffer.offer_image_url" max-width="300" />
        </v-col>
      </v-row>

      <v-container class="relative" style="max-width: 660px">
        <div class="ma-auto">
          <v-row align="center" justify="center" class="mt-2 mb-4">
            <v-col cols="11" class="py-0 text-center">
              <h1 class="header">{{ safeOfferType }}</h1>
            </v-col>
          </v-row>

          <v-row align="center" justify="center" class="my-4">
            <v-col cols="11" class="py-0 text-center">
              <h1 class="offer-summary" data-cy="swayOfferSummary">{{ swayOffer.offer_summary }}</h1>
            </v-col>
          </v-row>

          <v-row align="center" justify="center" class="mt-7 mb-2">
            <v-col cols="11" class="py-0 mt-3">
              <h1 class="header">{{ safeHeader }}</h1>
            </v-col>
          </v-row>

          <v-row align="center" justify="center" class="mt-4 mb-2">
            <v-col cols="11" class="py-0">
              <v-divider />
            </v-col>
          </v-row>

          <div v-if="isReceiptGiveAwayOffer">
            <ClaimSwayOfferSection :step-number="1" :step-title="safeUploadHeader" :step-sub-title="safeUploadSubTitle" />

            <v-row align="center" justify="center" class="mt-4">
              <v-col cols="11" class="pt-0 text-center">
                <v-img :src="require('@/assets/reverseFlow/curved_arrow.png')" :width="45" :height="40" :eager="true" contain class="step-arrow" />

                <PhotoUpload
                  ref="photoUploadForm"
                  photo-type="reverseFlowProof"
                  @photo-upload-selected="handlePhotoSelected"
                  @photo-upload-complete="handlePhotoUploaded"
                >
                  <div class="caption">Upload a photo of your receipt</div>
                </PhotoUpload>
              </v-col>
            </v-row>
          </div>

          <div v-else>
            <ClaimSwayOfferSection
              :step-number="1"
              step-title="Share your experience on social"
              step-sub-title="Make a post on your preferred social platform."
            >
              <ClaimSwayOfferTaskOption icon="Facebook" title="Facebook post, stories, checkin" :mentions="facebookMentions" :hashtags="hashtags" />
              <ClaimSwayOfferTaskOption icon="Instagram" title="Instagram post or story" :mentions="instagramMentions" :hashtags="hashtags" />
              <ClaimSwayOfferTaskOption icon="TikTok" title="Tik Tok video" />
              <ClaimSwayOfferTaskOption icon="Google" title="Google review" :url="googleReviewUrl" />
              <ClaimSwayOfferTaskOption icon="Facebook" title="Facebook review" />

              <v-row justify="start" class="mx-0 mt-0">
                <v-col cols="12" align="start" class="pl-1 pt-0">
                  <span class="mt-4 d-block text-decoration-underline blue--text" style="cursor: pointer" @click="showExamplesModal = true">
                    See examples of post and reviews
                  </span>
                </v-col>
              </v-row>
            </ClaimSwayOfferSection>

            <ClaimSwayOfferSection :step-number="2" step-title="Screenshot your post" step-sub-title="Take a screenshot of the post you made.">
              <v-row justify="start" class="mx-0 mt-0">
                <v-col cols="12" align="start" class="pl-1 pt-0">
                  <span class="mt-4 d-block text-decoration-underline blue--text" style="cursor: pointer" @click="showScreenshotExamplesModal = true">
                    See examples of screenshots
                  </span>
                </v-col>
              </v-row>
            </ClaimSwayOfferSection>

            <ClaimSwayOfferSection :step-number="3" :step-title="safeUploadHeader" :step-sub-title="safeUploadSubTitle" />

            <v-row align="center" justify="center" class="mt-4">
              <v-col cols="11" class="pt-0 text-center">
                <v-img :src="require('@/assets/reverseFlow/curved_arrow.png')" :width="45" :height="40" :eager="true" contain class="step-arrow" />

                <PhotoUpload
                  ref="photoUploadForm"
                  photo-type="reverseFlowProof"
                  @photo-upload-selected="handlePhotoSelected"
                  @photo-upload-complete="handlePhotoUploaded"
                />
              </v-col>
            </v-row>

            <ClaimSwayOfferSection
              v-if="isLocalOffer"
              :step-number="4"
              step-title="Use your voucher"
              step-sub-title="Your voucher will appear after submitting your screenshot. Show voucher to employee to receive your offer."
            />
          </div>

          <v-row align="center" justify="center" class="mt-10">
            <v-col cols="11" class="py-0" align="center">
              <div class="font-weight-bold">Questions?</div>
              <div class="font-weight-light">Email team@localfluence.com</div>
            </v-col>
          </v-row>

          <v-row v-if="swayOffer.fine_print || swayOffer.fine_print_url" align="center" justify="center" class="mt-10">
            <v-col cols="11" class="py-0" align="center">
              <div class="font-weight-bold" data-cy="finePrintHeader">{{ safeOfferType }} Details</div>
              <div v-if="swayOffer.fine_print" class="font-weight-light" data-cy="finePrint">{{ swayOffer.fine_print }}</div>
              <div v-if="swayOffer.fine_print_url" class="mt-1 mb-2 caption font-weight-light">
                <v-icon dense small color="primary" class="mt-n1 pr-1">mdi-open-in-new</v-icon>
                <a :href="swayOffer.fine_print_url" target="_blank" data-cy="finePrintLink">full terms and conditions</a>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-container>

      <div class="mt-10 pt-6 text-center grey lighten-4">
        <p class="text-h4 font-weight-bold">Your Impact</p>
        <div class="font-italic font-weight-light bottom-banner">92% of people trust a friends recommendation above all other forms of marketing</div>
        <div class="mt-3 text-h6 font-weight-bold">
          #LocalLove
          <br />#SupportLocal
        </div>
        <v-img :src="require('@/assets/survey/buildings.png')" :eager="true" max-width="360" class="ma-auto mt-7" />
      </div>
    </div>

    <v-footer v-if="photoSelected && !giveAwayProofId && !showEmailPrompt && !showWebComplete" app :fixed="true" :elevation="6" class="white">
      <v-row class="py-3">
        <v-col cols="12">
          <v-btn
            :loading="submitButtonLoading"
            block
            rounded
            color="primary"
            data-cy="shareButton"
            :class="submitButtonLoading ? '' : 'wiggle'"
            @click="submitToBusiness()"
          >
            Submit to Business
          </v-btn>
        </v-col>
      </v-row>
    </v-footer>
  </div>
</template>

<script>
import Vue from 'vue'
import { mapGetters } from 'vuex'

import { ApiService } from '@/services'

import confetti from 'canvas-confetti'

import PhotoUpload from '@/components/PhotoUpload.vue'
import TaskExamplesModal from '@/components/TaskExamplesModal'
import ScreenshotExamplesModal from '@/components/ScreenshotExamplesModal'
import ClaimSwayOfferSection from '@/components/sway/ClaimSwayOfferSection'
import ClaimSwayOfferTaskOption from '@/components/sway/ClaimSwayOfferTaskOption'
import GiveAwayNotificationModal from '@/components/GiveAwayNotificationModal'
import SwayOfferNotificationModal from '@/components/SwayOfferNotificationModal'

export default Vue.extend({
  name: 'ClaimSwayOffer',

  components: {
    PhotoUpload,
    TaskExamplesModal,
    ScreenshotExamplesModal,
    ClaimSwayOfferSection,
    ClaimSwayOfferTaskOption,
    GiveAwayNotificationModal,
    SwayOfferNotificationModal,
  },

  metaInfo() {
    return this.swayOffer
      ? {
          title: `${this.swayOffer.offer_summary} | ${this.swayOffer.business.brand}`,
        }
      : {
          title: 'Localfluence',
        }
  },

  props: {
    addressId: {
      type: String,
      default: null,
    },
    swayOfferId: {
      type: String,
      required: true,
    },
  },

  data: () => ({
    error: false,
    loading: true,
    photoSelected: false,
    swayOffer: null,
    hashtags: [],
    instagramMentions: [],
    facebookMentions: [],
    googleReviewUrl: null,
    showWebComplete: false,
    giveAwayProofId: null,
    showExamplesModal: false,
    showScreenshotExamplesModal: false,
    submitButtonLoading: false,
    emailAddress: null,
    showEmailPrompt: false,
  }),

  computed: {
    ...mapGetters('auth', ['loggedIn']),
    businessLogoUrl: function () {
      return this.swayOffer?.business?.logo_url
    },
    safeHeader: function () {
      if (this.isGiveAwayOffer) {
        return 'How To Enter This Giveaway'
      }

      return 'How To Receive This Offer'
    },
    safeUploadHeader: function () {
      if (this.isReceiptGiveAwayOffer) {
        return 'Upload your receipt below to receive 1 giveaway entry'
      }

      if (this.isWebGiveAwayOffer) {
        return 'Upload your screenshot below to receive 1 giveaway entry'
      }

      return 'Upload your screenshot below to unlock the offer'
    },
    safeUploadSubTitle: function () {
      if (this.isReceiptGiveAwayOffer) {
        return `Tap the box below to upload the photo of your receipt. You'll receive 1 entry into the contest after your receipt is submitted.`
      }

      const subTitle = 'Tap the box below to upload the screenshot of your post.'

      if (this.isWebGiveAwayOffer) {
        return `${subTitle} You'll receive 1 entry into the contest after your screenshot is submitted.`
      }

      if (this.isWebOffer) {
        return `${subTitle} Your reward will be unlocked immediately after the business approves your screenshot.`
      }

      return `${subTitle} The offer will unlock after submitting the screenshot.`
    },
    safeOfferType: function () {
      return this.isGiveAwayOffer ? 'Giveaway' : 'Offer'
    },
    swayOfferTaskType: function () {
      return {
        title: 'Post or Review',
        examples: [
          'https://storage.googleapis.com/localfluence-library-production/socialPostExamples/reverse_flow_example_1-20200720.png',
          'https://storage.googleapis.com/localfluence-library-production/socialPostExamples/reverse_flow_example_2-20200720.png',
          'https://storage.googleapis.com/localfluence-library-production/socialPostExamples/reverse_flow_example_3-20200720.png',
          'https://storage.googleapis.com/localfluence-library-production/socialPostExamples/reverse_flow_example_4-20200720.png',
        ],
        helpText: {
          socialTags: this.hashtags.length > 0,
          socialMention: this.instagramMentions.length > 0 || this.facebookMentions.length > 0,
        },
      }
    },
    isWebOffer() {
      return this.swayOffer?.offer_type === 'WEB'
    },
    isWebGiveAwayOffer() {
      return this.swayOffer?.offer_type === 'WEB_GIVE_AWAY'
    },
    isReceiptGiveAwayOffer() {
      return this.swayOffer?.offer_type === 'RECEIPT_GIVE_AWAY'
    },
    isGiveAwayOffer() {
      return this.isWebGiveAwayOffer || this.isReceiptGiveAwayOffer
    },
    isLocalOffer() {
      return this.swayOffer?.offer_type === 'LOCAL'
    },
    routeHasEmailQueryParam() {
      return this.$route.query.hasOwnProperty('email')
    },
  },

  mounted: function () {
    const url = this.routeHasEmailQueryParam
      ? `reverseFlow/offer/web/${this.swayOfferId}?emailAddress=${encodeURIComponent(this.$route.query.email)}`
      : `reverseFlow/offer/${this.swayOfferId}/${this.addressId ? this.addressId : ''}`

    ApiService.get(url)
      .then((response) => {
        if (response.data.error) {
          this.showWebComplete = response.data.error === 'OFFER_COMPLETE'
          this.error = true
          return
        }

        this.swayOffer = response.data
        this.hashtags = [...response.data.hashtags, ...(response.data.address?.hashtags || [])]
        this.instagramMentions = [...response.data.instagram_mentions, ...(response.data.address?.instagram_mentions || [])]
        this.facebookMentions = [...response.data.facebook_mentions, ...(response.data.address?.facebook_mentions || [])]
        this.googleReviewUrl = response.data.address ? response.data.address.google_review_url : null
        this.emailAddress = this.$route.query.email
      })
      .finally(() => {
        this.loading = false
      })
  },

  methods: {
    handlePhotoSelected() {
      this.photoSelected = true
      this.submitButtonLoading = false
    },
    handleEmailAddressEntered(notificationData) {
      this.showEmailPrompt = false
      this.emailAddress = notificationData.email
      this.$refs.photoUploadForm.uploadImage()
    },
    handlePhotoUploaded(publicUrl) {
      ApiService.post(`reverseFlow/proof`, {
        swayOfferId: this.swayOffer.sway_offer_id,
        addressId: this.addressId,
        emailAddress: this.emailAddress,
        proofImageUrl: publicUrl,
      })
        .then((response) => {
          if (response.data.error) {
            throw new Error(response.data.error)
          }

          confetti({
            zIndex: 1000,
            particleCount: 75,
          })

          this.$toast.success('Offer unlocked!')

          if (this.isWebOffer) {
            this.showWebComplete = true
          } else if (this.isGiveAwayOffer) {
            this.giveAwayProofId = response.data.reverseFlowProofId
          } else {
            this.$router.replace({ path: `/swayOffer/redeem/${response.data.reverseFlowProofId}` })
          }
        })
        .catch((e) => {
          this.$toast.error(e)
          this.submitButtonLoading = false
          this.emailAddress = this.$route.query.email
        })
    },
    chooseImage() {
      if (!this.$refs.photoUploadForm) {
        this.$toast.error('Uh-oh! There was a problem uploading your photo. Try again.')
        return
      }

      this.submitButtonLoading = true

      this.$refs.photoUploadForm.chooseImage()
    },
    submitToBusiness() {
      if (!this.$refs.photoUploadForm) {
        this.$toast.error('Uh-oh! There was a problem uploading your photo. Try again.')
        return
      }

      this.submitButtonLoading = true

      if (this.routeHasEmailQueryParam && !this.emailAddress) {
        this.showEmailPrompt = true
      } else {
        this.$refs.photoUploadForm.uploadImage()
      }
    },
  },
})
</script>

<style lang="scss" scoped>
.bottom-banner {
  max-width: 250px;
  margin-left: auto;
  margin-right: auto;
}

.step-arrow {
  display: inline-block;
  margin-bottom: 10px;
  transform: rotate(30deg);
  -ms-transform: rotate(30deg);
  -webkit-transform: rotate(30deg);
}

.offer-summary {
  font-size: 28px;
  font-weight: 700;
  line-height: 1.1em;
}

.header {
  font-size: 20px;
  font-weight: 700;
  line-height: 1.1em;
}

.offer-complete-circle {
  border-radius: 50%;
  width: 71px;
  height: 71px;
  padding: 14px;
  margin-top: -15px;
  margin-bottom: -10px;
  background: #fff;
  text-align: center;
  border: 1px solid #009200;
  color: #009200;
}

//
.wiggle {
  animation: wiggle 2s infinite;
  -webkit-animation: wiggle 2s infinite;
  -moz-animation: wiggle 2 infinite;
}

@keyframes wiggle {
  0% {
    transform: rotate(0deg);
  }
  80% {
    transform: rotate(0deg);
  }
  85% {
    transform: rotate(5deg);
  }
  95% {
    transform: rotate(-5deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

@-webkit-keyframes wiggle {
  0% {
    transform: rotate(0deg);
  }
  80% {
    transform: rotate(0deg);
  }
  85% {
    transform: rotate(5deg);
  }
  95% {
    transform: rotate(-5deg);
  }
  100% {
    transform: rotate(0deg);
  }
}

@-moz-keyframes wiggle {
  0% {
    transform: rotate(0deg);
  }
  80% {
    transform: rotate(0deg);
  }
  85% {
    transform: rotate(5deg);
  }
  95% {
    transform: rotate(-5deg);
  }
  100% {
    transform: rotate(0deg);
  }
}
</style>
